import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';
import { MouseEvent } from 'react';

import Tasks from 'APP/Tasks';
import { SecretChatRequestPopup } from 'APP/main/PopupManager/Popups/SecretChatRequestPopup/SecretChatRequestPopup';
import { BackgroundCall } from 'STORE/BackgroundCalls/BackgroundCall/BackgroundCall';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';
import { Group } from 'STORE/Groups/Group';
import { SecretGroup } from 'STORE/Groups/SecretGroup/SecretGroup';

interface IGroupItemPresenter {
  hasMention: boolean;
  hasUnread: boolean;
  numberOfUnread: number;
  hasPinedIcon: boolean;
  backgroundCall: BackgroundCall | null;
  activeCall: MainCall | null;
  isBlockedSecretChat: boolean;
  hasSecretChatAccessRequest: boolean;
  onSecureChatUnlockClick(event: MouseEvent<HTMLButtonElement>): void;
  onJoinToCall(): void;
}

export const useGroupItemPresenter = (group: Group): IGroupItemPresenter => {
  const source = useAsObservableSource({ group });

  const presenter = useLocalStore<IGroupItemPresenter>(() => ({
    get hasMention(): boolean {
      return source.group.unreadUserMentionsCounter > 0;
    },

    get hasUnread(): boolean {
      return source.group.numberOfUnread > 0 || source.group.unread;
    },

    get numberOfUnread(): number {
      return source.group.numberOfUnread;
    },

    get hasPinedIcon(): boolean {
      return source.group.isPinned;
    },

    get backgroundCall(): BackgroundCall | null {
      return source.group.backgroundCall;
    },

    get activeCall(): MainCall | null {
      return source.group.activeCall || null;
    },

    get isBlockedSecretChat(): boolean {
      return source.group instanceof SecretGroup && !source.group.isUnlocked;
    },

    get hasSecretChatAccessRequest(): boolean {
      return (
        source.group instanceof SecretGroup &&
        !!source.group.chatAccessRequestsCount &&
        source.group.withMeInAdmins
      );
    },

    onJoinToCall(): void {
      if (!presenter.backgroundCall) {
        return;
      }

      Tasks.calls.joinToCall({
        groupId: source.group.id,
        channelId: presenter.backgroundCall.channelId,
        withAudio: source.group.isP2P,
        withVideo: false,
        providerType: presenter.backgroundCall.callProvider,
      });
    },

    onSecureChatUnlockClick(event: MouseEvent<HTMLButtonElement>): void {
      event.stopPropagation();
      event.preventDefault();
      Tasks.app.openPopup(SecretChatRequestPopup, { params: { groupId: source.group.id } });
    },
  }));

  return presenter;
};
